import { ProDetailList } from '@bedrock/eed-react';
import {
  FormCard,
  FormItemAdvanceFunding,
  FormItemCurrency,
  FormItemFinanceAttachmentFile,
  FormItemFinanceAttachmentType,
  FormItemFinanceDetailList,
  FormItemFiscalVBankCash,
  FormItemGameBusinessRevenue,
  FormItemGroupBusinessRevenue,
  FormItemIncomeNumber,
  FormItemNetEaseGameBusinessRevenue,
  FormItemPaidUpCapitalNumber,
  FormItemProfitNumber,
  FormItemTotalAssetsNumber,
  FormItemYearPicker,
  FormItemQccAnnualAssetDetailList,
  DataSourceTag,
} from '@purchase/supplier';
import { Radio, Tag, Popover } from '@bedrock/components';
import { CaretRight } from '@bedrock/icons-react';
import React, { useEffect, useState } from 'react';
import './index.less';

type FinanceSource = 'supplier' | 'thirdParty';

const FinancialInfo = (props: { formDetail: any; isExternal?: boolean }) => {
  const { formDetail, isExternal = false } = props;
  const hasFinanceInfos = !!formDetail?.vendorFinanceInfo?.financeInfos?.length;
  const financeAbsenceReason =
    formDetail?.vendorFinanceInfo?.financeAbsenceReason;

  const supplierCount =
    formDetail?.vendorFinanceInfo?.financeInfos?.length ?? 0;
  const thirdPartyCount = formDetail?.vendorQccAnnualAssetInfos?.length ?? 0;
  const qccAssetsUpdateTime = formDetail?.qccDataFetchedAt;

  const [financeSource, setFinanceSource] = useState<FinanceSource>('supplier');

  // 智能默认 tab:单方有数据时自动切换
  useEffect(() => {
    if (supplierCount > 0 && thirdPartyCount === 0) {
      setFinanceSource('supplier');
    } else if (supplierCount === 0 && thirdPartyCount > 0) {
      setFinanceSource('thirdParty');
    }
  }, [supplierCount, thirdPartyCount]);

  const getAbsenceTag = () => {
    if (hasFinanceInfos) return null;
    // 有缺失原因
    if (financeAbsenceReason) {
      return (
        <Popover
          title="缺失原因"
          content={financeAbsenceReason}
          placement="bottom"
        >
          <Tag
            size="xSmall"
            color="orange"
            text={
              <span>
                财务状况缺失 <CaretRight />
              </span>
            }
          />
        </Popover>
      );
    }
    // 无缺失原因
    return <Tag size="xSmall" color="orange" text="财务状况缺失" />;
  };

  return (
    <FormCard
      title="财务状况"
      className={'mpc-supplier-new-detail-financial-info'}
      leftSlot={
        isExternal || financeSource === 'supplier' ? getAbsenceTag() : null
      }
    >
      {!isExternal && (
        <div className="flex items-center justify-between">
          <Radio.Group
            value={financeSource}
            onChange={(e) => setFinanceSource(e.target.value)}
          >
            <Radio.Button value="supplier">
              供应商录入 ({supplierCount})
            </Radio.Button>
            <Radio.Button value="thirdParty">
              企查查获取 ({thirdPartyCount})
            </Radio.Button>
          </Radio.Group>
          {financeSource === 'thirdParty' && (
            <DataSourceTag source="企查查" updateTime={qccAssetsUpdateTime} />
          )}
        </div>
      )}

      {(isExternal || financeSource === 'supplier') && (
        <>
          {/* 财务指标卡片网格 */}
          <div className="mpc-supplier-new-detail-financial-info-descriptions">
            <div className="mpc-supplier-new-detail-financial-info-descriptions-item">
              <FormItemAdvanceFunding
                label="可垫资量级"
                name={['vendorFinanceInfo', 'advanceFunding']}
              />
            </div>
            <div className="mpc-supplier-new-detail-financial-info-descriptions-item">
              <FormItemFiscalVBankCash
                label="最近一年度末现金及银行存款"
                name={['vendorFinanceInfo', 'fiscalVbankCash']}
                fieldProps={{
                  highlight: true,
                  showUnitIndicator: true,
                  style: { color: 'var(--status-warning-1)' },
                }}
              />
            </div>
            <div className="mpc-supplier-new-detail-financial-info-descriptions-item">
              <FormItemGroupBusinessRevenue
                label="网易集团业务收入占比"
                name={['vendorFinanceInfo', 'groupBusinessRevenue']}
              />
            </div>
            <div className="mpc-supplier-new-detail-financial-info-descriptions-item">
              <FormItemGameBusinessRevenue
                label="游戏业务收入占比"
                name={['vendorFinanceInfo', 'gameBusinessRevenue']}
              />
            </div>
            <div className="mpc-supplier-new-detail-financial-info-descriptions-item">
              <FormItemNetEaseGameBusinessRevenue
                label="网易游戏业务收入占比"
                name={['vendorFinanceInfo', 'netEaseGameBusinessRevenue']}
              />
            </div>
          </div>

          {!financeAbsenceReason && (
            <FormItemFinanceDetailList
              name={['vendorFinanceInfo', 'financeInfos']}
              reminderDisabled
              fieldProps={{ scroll: { x: 1120 } }}
            >
              <ProDetailList.Item
                label="#"
                name="#"
                meta={{ columnProps: { width: 50, fixed: true } }}
              >
                {(value, record, index) => {
                  return index + 1;
                }}
              </ProDetailList.Item>
              <FormItemYearPicker
                name="year"
                label="年份"
                meta={{
                  columnProps: {
                    width: 80,
                    className: 'font-semibold',
                    fixed: true,
                  },
                }}
              />
              <FormItemCurrency
                name="currency"
                label="币种"
                meta={{ columnProps: { width: 100 } }}
              />
              <FormItemIncomeNumber name="income" label="营业收入 (万)" />
              <FormItemProfitNumber name="profit" label="利润总额 (万)" />
              <FormItemPaidUpCapitalNumber
                name="paidUpCapital"
                label="实缴资本 (万)"
              />
              <FormItemTotalAssetsNumber
                name="totalAssets"
                label="总资产 (万)"
              />
              <FormItemFinanceAttachmentType
                name="financeAttachmentFileType"
                label="上传类型"
                meta={{ columnProps: { width: 160 } }}
              />
              <FormItemFinanceAttachmentFile
                name="attachmentFile"
                label="附件"
                meta={{ columnProps: { width: 240 } }}
              />
            </FormItemFinanceDetailList>
          )}
        </>
      )}

      {!isExternal && financeSource === 'thirdParty' && (
        <FormItemQccAnnualAssetDetailList
          name="vendorQccAnnualAssetInfos"
          fieldProps={{ scroll: { x: 1146 } }}
        />
      )}
    </FormCard>
  );
};

export default FinancialInfo;
